
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Network,
  Wifi,
  BarChart,
  Filter,
  Search,
  ChevronRight,
  LayoutGrid,
  List,
  Plus,
  Brain,
  RefreshCw,
  Signal,
  Server,
  Cloud
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PageTransition from '@/components/layout/PageTransition';
import { NetworkType, NetworkProvider, NetworkInfrastructure, ExtendedNetworkDefinition } from '@/components/dashboard/network/NetworkTypes';
import { mockNetworks, getNetworkById } from '@/data/mockNetworks';
import NetworkTypeIcon from '@/components/dashboard/network/NetworkTypeIcon';
import NetworkGraph from '@/components/dashboard/NetworkGraph';
import MeshNetworkGraph from '@/components/dashboard/MeshNetworkGraph';
import DeviceStatus from '@/components/dashboard/DeviceStatus';
import NetworkSelector from '@/components/dashboard/NetworkSelector';
import { useTheme } from '@/components/theme/ThemeProvider';
import { toast } from 'sonner';

const Networks = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Network list view state
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const [filteredNetworks, setFilteredNetworks] = useState<ExtendedNetworkDefinition[]>(mockNetworks);
  const [filterType, setFilterType] = useState<NetworkType | 'all'>('all');
  const [filterProvider, setFilterProvider] = useState<NetworkProvider | 'all'>('all');
  const [filterInfrastructure, setFilterInfrastructure] = useState<NetworkInfrastructure | 'all'>('all');

  // Network health state
  const [selectedNetworkId, setSelectedNetworkId] = useState<string>(mockNetworks[0].id);
  const selectedNetwork = getNetworkById(selectedNetworkId) as ExtendedNetworkDefinition;
  
  // New network state
  const [showNewForm, setShowNewForm] = useState(false);
  const [newNetwork, setNewNetwork] = useState({
    name: '',
    type: 'mesh' as NetworkType,
    provider: 'aws' as NetworkProvider,
    infrastructure: 'vm' as NetworkInfrastructure
  });
  
  // AI actions state
  const [isAiAnalyzing, setIsAiAnalyzing] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);

  // Apply filters when they change
  useEffect(() => {
    let results = [...mockNetworks];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      results = results.filter(network => 
        network.name.toLowerCase().includes(query) ||
        network.type.toLowerCase().includes(query) ||
        network.provider.toLowerCase().includes(query)
      );
    }
    
    // Apply type filter
    if (filterType !== 'all') {
      results = results.filter(network => network.type === filterType);
    }
    
    // Apply provider filter
    if (filterProvider !== 'all') {
      results = results.filter(network => network.provider === filterProvider);
    }
    
    // Apply infrastructure filter
    if (filterInfrastructure !== 'all') {
      results = results.filter(network => network.infrastructure === filterInfrastructure);
    }
    
    setFilteredNetworks(results);
  }, [searchQuery, filterType, filterProvider, filterInfrastructure]);

  // Get unique values for filters
  const uniqueTypes = ['all', ...new Set(mockNetworks.map(n => n.type))];
  const uniqueProviders = ['all', ...new Set(mockNetworks.map(n => n.provider))];
  const uniqueInfrastructures = ['all', ...new Set(mockNetworks.map(n => n.infrastructure))];

  // Calculate network health score based on packet loss and signal strength
  const calculateHealthScore = () => {
    const { packetLoss, signalStrength } = selectedNetwork.metrics;
    // Lower packet loss is better, higher signal strength is better
    const packetLossScore = 100 - (packetLoss * 10); // 0% loss = 100 points, 10% loss = 0 points
    const signalStrengthScore = signalStrength; // Direct mapping
    
    // Weighted average: 40% packet loss, 60% signal strength
    const healthScore = Math.round((packetLossScore * 0.4) + (signalStrengthScore * 0.6));
    return Math.min(100, Math.max(0, healthScore)); // Clamp between 0-100
  };

  const networkHealth = calculateHealthScore();

  // Helper to format status
  const getStatusBadge = (isActive: boolean) => {
    if (isActive) {
      return <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">Active</Badge>;
    }
    return <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">Inactive</Badge>;
  };

  // Event handlers
  const handleNetworkChange = (networkId: string) => {
    setSelectedNetworkId(networkId);
    const network = getNetworkById(networkId) as ExtendedNetworkDefinition;
    toast.info(`Switched to ${network.name}`, {
      description: `Displaying metrics for ${network.type.toUpperCase()} network`
    });
  };

  const handleCreateNetwork = () => {
    const newId = `net-${Date.now().toString().slice(-4)}`;
    const createdNetwork = {
      id: newId,
      name: newNetwork.name,
      type: newNetwork.type,
      provider: newNetwork.provider,
      infrastructure: newNetwork.infrastructure,
      region: 'US-West',
      nodes: 1,
      isActive: true,
      createdAt: new Date().toISOString(),
      status: 'active' as const,
      metrics: {
        packetLoss: 0.5,
        signalStrength: 95,
        latency: 15
      }
    };
    
    // Add to mockNetworks - in a real app we would call an API
    mockNetworks.push(createdNetwork);
    setFilteredNetworks([...filteredNetworks, createdNetwork]);
    setShowNewForm(false);
    setNewNetwork({
      name: '',
      type: 'mesh',
      provider: 'aws',
      infrastructure: 'vm'
    });
    
    toast.success("Network created", {
      description: `${newNetwork.name} has been successfully created.`
    });
    
    // Simulate AI analysis of the new network
    runAiAnalysis(newId);
  };

  const handleRefreshData = () => {
    toast.success("Network data refreshed", {
      description: "Latest metrics have been loaded"
    });
  };

  const runAiAnalysis = (networkId: string) => {
    const network = networkId === 'all' 
      ? { name: 'All Networks' } 
      : mockNetworks.find(n => n.id === networkId);
      
    if (!network) return;
    
    setIsAiAnalyzing(true);
    
    // Simulate AI processing
    setTimeout(() => {
      // Generate contextual AI suggestions based on network type
      const suggestions = [];
      
      if (networkId === 'all') {
        suggestions.push(
          "Optimize bandwidth allocation across all networks",
          "Implement cross-network security scanning",
          "Analyze traffic patterns to predict potential bottlenecks"
        );
      } else {
        const targetNetwork = mockNetworks.find(n => n.id === networkId);
        if (targetNetwork?.type === 'mesh') {
          suggestions.push(
            "Add additional mesh nodes to improve coverage",
            "Reconfigure mesh topology for more efficient routing",
            "Update firmware on existing mesh nodes"
          );
        } else if (targetNetwork?.type === '5g') {
          suggestions.push(
            "Analyze 5G coverage gaps in your deployment area",
            "Optimize for frequency interference from nearby networks",
            "Deploy edge computing resources for latency reduction"
          );
        } else if (targetNetwork?.type === 'fiber') {
          suggestions.push(
            "Schedule inspection of physical fiber connections",
            "Add redundant fiber pathways for critical segments",
            "Upgrade ONT devices to latest generation"
          );
        }
      }
      
      setAiSuggestions(suggestions);
      setIsAiAnalyzing(false);
      
      toast.success(`AI analysis complete for ${network.name}`, {
        description: "View the AI recommendations below"
      });
    }, 2500);
  };
  
  const applyAiRecommendation = (recommendation: string) => {
    toast.success("AI recommendation applied", {
      description: `Implementation plan for "${recommendation}" has been added to your automation queue.`
    });
    
    // Redirect to AI Automation page with this task
    navigate('/ai-automation', { 
      state: { 
        activateTab: 'automations',
        task: recommendation 
      } 
    });
  };

  return (
    <PageTransition>
      <Helmet>
        <title>Networks | Network Management</title>
      </Helmet>
      <div className="container p-4 md:p-6">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold">Networks</h1>
            <p className="text-muted-foreground">Manage, monitor and configure your networks</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowNewForm(!showNewForm)} className="flex items-center">
              <Plus className="mr-2 h-4 w-4" />
              Add Network
            </Button>
            <Button 
              variant="outline" 
              onClick={() => runAiAnalysis('all')} 
              className="flex items-center"
              disabled={isAiAnalyzing}
            >
              {isAiAnalyzing ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Brain className="mr-2 h-4 w-4" />
              )}
              AI Analysis
            </Button>
          </div>
        </div>

        {showNewForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Create New Network</CardTitle>
              <CardDescription>Define your network configuration</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="name">Network Name</Label>
                  <Input 
                    id="name" 
                    value={newNetwork.name} 
                    onChange={(e) => setNewNetwork({...newNetwork, name: e.target.value})}
                    placeholder="e.g. Production Mesh Network"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="type">Network Type</Label>
                    <Select 
                      value={newNetwork.type} 
                      onValueChange={(value) => setNewNetwork({...newNetwork, type: value as NetworkType})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mesh">Mesh Network</SelectItem>
                        <SelectItem value="5g">5G Network</SelectItem>
                        <SelectItem value="fiber">Fiber Network</SelectItem>
                        <SelectItem value="cloud">Cloud Network</SelectItem>
                        <SelectItem value="hybrid">Hybrid Network</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="provider">Cloud Provider</Label>
                    <Select 
                      value={newNetwork.provider} 
                      onValueChange={(value) => setNewNetwork({...newNetwork, provider: value as NetworkProvider})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aws">AWS</SelectItem>
                        <SelectItem value="google">Google Cloud</SelectItem>
                        <SelectItem value="azure">Azure</SelectItem>
                        <SelectItem value="att">AT&T</SelectItem>
                        <SelectItem value="verizon">Verizon</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="infrastructure">Infrastructure Type</Label>
                  <Select 
                    value={newNetwork.infrastructure} 
                    onValueChange={(value) => setNewNetwork({...newNetwork, infrastructure: value as NetworkInfrastructure})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select infrastructure" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vm">Virtual Machines</SelectItem>
                      <SelectItem value="container">Containers</SelectItem>
                      <SelectItem value="dedicated">Dedicated Hardware</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => setShowNewForm(false)}>Cancel</Button>
              <Button onClick={handleCreateNetwork} disabled={!newNetwork.name}>Create Network</Button>
            </CardFooter>
          </Card>
        )}

        {aiSuggestions.length > 0 && (
          <Card className="mb-8 border-primary/20 bg-primary/5">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center">
                <Brain className="mr-2 h-5 w-5 text-primary" />
                AI Recommendations
              </CardTitle>
              <CardDescription>Intelligent suggestions for network optimization</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {aiSuggestions.map((suggestion, index) => (
                  <li key={index} className="flex justify-between items-center">
                    <span>{suggestion}</span>
                    <Button 
                      size="sm" 
                      onClick={() => applyAiRecommendation(suggestion)}
                    >
                      Apply
                    </Button>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="list" className="space-y-6">
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="list" className="flex items-center">
              <Network className="mr-2 h-4 w-4" />
              Network List
            </TabsTrigger>
            <TabsTrigger value="health" className="flex items-center">
              <Signal className="mr-2 h-4 w-4" />
              Network Health
            </TabsTrigger>
            <TabsTrigger value="config" className="flex items-center">
              <Server className="mr-2 h-4 w-4" />
              Configuration
            </TabsTrigger>
          </TabsList>

          {/* Network List Tab */}
          <TabsContent value="list" className="space-y-6">
            {/* Filters and search */}
            <div className="grid gap-4 mb-8 md:grid-cols-2 lg:grid-cols-4">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search networks..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
              
              <Select value={filterType} onValueChange={value => setFilterType(value as NetworkType | 'all')}>
                <SelectTrigger>
                  <div className="flex items-center">
                    <Filter className="h-4 w-4 mr-2" />
                    <span>{filterType === 'all' ? 'All Types' : filterType}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {uniqueTypes.map(type => (
                    <SelectItem key={type} value={type}>
                      {type === 'all' ? 'All Types' : type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterProvider} onValueChange={value => setFilterProvider(value as NetworkProvider | 'all')}>
                <SelectTrigger>
                  <div className="flex items-center">
                    <Filter className="h-4 w-4 mr-2" />
                    <span>{filterProvider === 'all' ? 'All Providers' : filterProvider}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {uniqueProviders.map(provider => (
                    <SelectItem key={provider} value={provider}>
                      {provider === 'all' ? 'All Providers' : provider}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'card' ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setViewMode('card')}
                  className="flex-1"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setViewMode('table')}
                  className="flex-1"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Network list */}
            {filteredNetworks.length === 0 ? (
              <div className="text-center p-8 border rounded-lg bg-card">
                <Network className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No networks found</h3>
                <p className="text-muted-foreground mb-4">
                  No networks match your current filters. Try adjusting your search or filters.
                </p>
                <Button onClick={() => {
                  setSearchQuery('');
                  setFilterType('all');
                  setFilterProvider('all');
                  setFilterInfrastructure('all');
                }}>
                  Reset Filters
                </Button>
              </div>
            ) : viewMode === 'card' ? (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {filteredNetworks.map(network => (
                  <Card key={network.id} className="overflow-hidden">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <NetworkTypeIcon type={network.type} className="h-5 w-5" />
                            {network.name}
                          </CardTitle>
                          <CardDescription className="mt-1">
                            {network.provider.toUpperCase()} | {network.type}
                          </CardDescription>
                        </div>
                        {getStatusBadge(network.isActive)}
                      </div>
                    </CardHeader>
                    <CardContent className="pb-2">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <p className="text-muted-foreground">Region</p>
                          <p className="font-medium">{network.region}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Infrastructure</p>
                          <p className="font-medium">{network.infrastructure}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Nodes</p>
                          <p className="font-medium">{network.nodes}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Created</p>
                          <p className="font-medium">{new Date(network.createdAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex justify-between pt-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => runAiAnalysis(network.id)}
                      >
                        <Brain className="h-4 w-4 mr-2" />
                        AI Analyze
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => handleNetworkChange(network.id)}
                      >
                        Details
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Region</TableHead>
                      <TableHead>Nodes</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredNetworks.map(network => (
                      <TableRow key={network.id}>
                        <TableCell className="font-medium">{network.name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <NetworkTypeIcon type={network.type} className="h-4 w-4" />
                            {network.type}
                          </div>
                        </TableCell>
                        <TableCell>{network.provider}</TableCell>
                        <TableCell>{network.region}</TableCell>
                        <TableCell>{network.nodes}</TableCell>
                        <TableCell>{getStatusBadge(network.isActive)}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                Actions
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleNetworkChange(network.id)}>
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem>Edit Network</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => runAiAnalysis(network.id)}>
                                AI Analysis
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive">Delete Network</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          {/* Network Health Tab */}
          <TabsContent value="health" className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
              <div>
                <h2 className="text-lg font-semibold">Network Health Monitor</h2>
                <p className="text-muted-foreground">
                  Monitor the performance and stability of your networks
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <NetworkSelector
                  networks={mockNetworks.map(network => ({
                    id: network.id,
                    name: network.name,
                    type: network.type,
                    status: network.status,
                    metrics: network.metrics
                  }))}
                  selectedNetworkId={selectedNetworkId}
                  onNetworkChange={handleNetworkChange}
                />
                <Button variant="outline" size="sm" onClick={handleRefreshData}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Data
                </Button>
              </div>
            </div>
            
            {/* Network Health Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium flex items-center">
                    Network Status
                  </CardTitle>
                  <CardDescription>Overall system health</CardDescription>
                </CardHeader>
                <CardContent>
                  <div 
                    className={`text-3xl font-bold ${
                      networkHealth > 80 
                        ? (isDark ? "text-[#00D25B]" : "text-green-500") 
                        : networkHealth > 50 
                          ? "text-amber-500" 
                          : "text-red-500"
                    }`}
                  >
                    {networkHealth}%
                  </div>
                  <Progress value={networkHealth} className="mt-2" />
                  <p className="text-xs text-muted-foreground mt-2">
                    Last updated: 2 minutes ago
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium flex items-center">
                    Latency
                  </CardTitle>
                  <CardDescription>Average response time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{selectedNetwork.metrics.latency}ms</div>
                  <div className="grid grid-cols-5 gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map((bar) => (
                      <div 
                        key={bar}
                        className={`h-1 rounded-full ${
                          bar <= Math.min(5, Math.ceil(5 - (selectedNetwork.metrics.latency / 50)))
                            ? (isDark ? "bg-[#00D25B]" : "bg-green-500") 
                            : "bg-gray-200"
                        }`}
                      ></div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {selectedNetwork.type === "fiber" 
                      ? "Fiber optic provides lowest latency" 
                      : selectedNetwork.type === "satcom" 
                        ? "Satellite communication has higher latency"
                        : "Average latency for this network type"}
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-medium flex items-center">
                    Current Issues
                  </CardTitle>
                  <CardDescription>Ongoing problems</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {selectedNetwork.status === 'maintenance' ? 5 : selectedNetwork.status === 'inactive' ? 8 : 3}
                  </div>
                  <div className="flex items-center gap-6 mt-2">
                    <div className="flex items-center gap-1.5">
                      <DeviceStatus status={selectedNetwork.status === 'inactive' ? "offline" : "warning"} animate={false} />
                      <span className="text-xs">
                        {selectedNetwork.status === 'inactive' ? '2 Offline' : '1 Offline'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <DeviceStatus status="warning" animate={false} />
                      <span className="text-xs">
                        {selectedNetwork.status === 'maintenance' ? '4 Warnings' : '2 Warnings'}
                      </span>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="link" 
                    className="p-0 h-auto text-xs mt-2"
                    onClick={() => runAiAnalysis(selectedNetworkId)}
                  >
                    Analyze issues with AI
                  </Button>
                </CardContent>
              </Card>
            </div>
            
            {/* Network Traffic Graph */}
            <div className="glass rounded-xl p-5 mb-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">{selectedNetwork.name} Traffic</h2>
                <div className="text-xs text-muted-foreground">Last 24 hours</div>
              </div>
              {selectedNetwork.type === 'mesh' ? (
                <div className="h-[300px] md:h-[400px]">
                  <MeshNetworkGraph />
                </div>
              ) : (
                <NetworkGraph />
              )}
            </div>
          </TabsContent>

          {/* Network Configuration Tab */}
          <TabsContent value="config" className="space-y-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-lg font-semibold">Configuration & Deployment</h2>
                <p className="text-muted-foreground">
                  Configure networks and deploy containers or VMs
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => navigate('/ai-automation')}>
                  <Brain className="h-4 w-4 mr-2" />
                  AI Automation Center
                </Button>
                <Button variant="outline" size="sm" onClick={() => runAiAnalysis(selectedNetworkId)}>
                  Analyze Configuration
                </Button>
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Cloud className="h-5 w-5 mr-2" />
                    Container Deployment
                  </CardTitle>
                  <CardDescription>Deploy and manage containers</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid gap-2">
                      <Label>Container Type</Label>
                      <Select defaultValue="docker">
                        <SelectTrigger>
                          <SelectValue placeholder="Select container type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="docker">Docker</SelectItem>
                          <SelectItem value="kubernetes">Kubernetes</SelectItem>
                          <SelectItem value="podman">Podman</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid gap-2">
                      <Label>Target Network</Label>
                      <Select defaultValue={selectedNetworkId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select network" />
                        </SelectTrigger>
                        <SelectContent>
                          {mockNetworks.map(network => (
                            <SelectItem key={network.id} value={network.id}>
                              {network.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid gap-2">
                      <Label>Image</Label>
                      <Input placeholder="e.g. nginx:latest" />
                    </div>
                    
                    <Button onClick={() => {
                      navigate('/ai-automation', { 
                        state: { 
                          activateTab: 'deployments',
                          task: "Deploy Container" 
                        } 
                      });
                    }} className="w-full">
                      Deploy Container
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center">
                    <Server className="h-5 w-5 mr-2" />
                    VM Deployment
                  </CardTitle>
                  <CardDescription>Deploy and manage virtual machines</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid gap-2">
                      <Label>VM Type</Label>
                      <Select defaultValue="kvm">
                        <SelectTrigger>
                          <SelectValue placeholder="Select VM type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kvm">KVM</SelectItem>
                          <SelectItem value="hyperv">Hyper-V</SelectItem>
                          <SelectItem value="vmware">VMware</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid gap-2">
                      <Label>Operating System</Label>
                      <Select defaultValue="ubuntu">
                        <SelectTrigger>
                          <SelectValue placeholder="Select OS" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ubuntu">Ubuntu</SelectItem>
                          <SelectItem value="debian">Debian</SelectItem>
                          <SelectItem value="centos">CentOS</SelectItem>
                          <SelectItem value="windows">Windows</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label>CPU Cores</Label>
                        <Input type="number" defaultValue="2" min="1" max="16" />
                      </div>
                      <div>
                        <Label>Memory (GB)</Label>
                        <Input type="number" defaultValue="4" min="1" max="64" />
                      </div>
                    </div>
                    
                    <Button onClick={() => {
                      navigate('/ai-automation', { 
                        state: { 
                          activateTab: 'deployments',
                          task: "Deploy VM" 
                        } 
                      });
                    }} className="w-full">
                      Deploy VM
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PageTransition>
  );
};

// Helper component for Label
const Label = ({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) => {
  return (
    <label htmlFor={htmlFor} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
      {children}
    </label>
  );
};

export default Networks;
